import { Sort } from '@compass/client/types/common';
import type { Evaluation, SectionScoreSort } from '@compass/client/types/evaluation';
import type { Scorecard, ScorecardSection } from '@compass/client/types/scorecard';
import { isAxiosError } from 'axios';

import type { SortState } from '@/types/table';

export interface SectionScoreColumn {
  // Scorecard section id — the key of the stored `section_scores` map on each list row.
  sectionId: string;
  header: string;
}

const getSectionHeader = (section: ScorecardSection, index: number) =>
  `${section.title?.trim() || `Section ${index + 1}`} %`;

// Section columns for the QC list (AQ-750/AQ-800): the union of the page's scorecard sections
// keyed by section id, in scorecard order, so scorecard versions sharing ids collapse into one
// column. A section without an id can never have a stored score, so it gets no column.
export const collectSectionScoreColumns = (
  pageRows: Evaluation[],
  scorecardsById: Map<string, Scorecard>,
): SectionScoreColumn[] => {
  const columns: SectionScoreColumn[] = [];
  const seen = new Set<string>();

  pageRows.forEach(row => {
    const scorecard = row.scorecard_id ? scorecardsById.get(row.scorecard_id) : undefined;
    scorecard?.sections?.forEach((section, index) => {
      if (section.id && !seen.has(section.id)) {
        seen.add(section.id);
        columns.push({ sectionId: section.id, header: getSectionHeader(section, index) });
      }
    });
  });

  return columns;
};

// Table sort field for a section column; the QC list maps it to `sort_by_section_score`.
export const SECTION_SCORE_SORT_FIELD_PREFIX = 'section_score:';

// URL param `sort_by_section_score=<section_id>:asc|desc`. Ids the backend would reject
// (empty, `.` or `$`) are ignored rather than sent.
export const parseSectionScoreSortParam = (
  value: string | null | undefined,
): SectionScoreSort | undefined => {
  const separator = value?.lastIndexOf(':') ?? -1;
  if (!value || separator <= 0) return undefined;
  const sectionId = value.slice(0, separator);
  const direction = value.slice(separator + 1);
  if (/[.$]/.test(sectionId) || (direction !== 'asc' && direction !== 'desc')) return undefined;
  return { section_id: sectionId, sort: direction === 'asc' ? Sort.SORT_ASC : Sort.SORT_DESC };
};

// The QC list's sorts are mutually exclusive: the clicked column's sort is set and the
// other params are cleared. GenericTable appends a newly clicked field after the active one,
// toggles the active one asc -> desc, and drops it on desc. call_start_time is the default sort
// and has no "unsorted" state, so dropping it flips its direction instead; dropping a score or
// duration sort falls back to that default.
const getClickedSort = (active: SortState | undefined, next: SortState[]) => {
  const clicked = next.find(sort => sort.field !== active?.field) ?? next[0];
  if (clicked || active?.field !== 'call_start_time') return clicked;
  return { field: active.field, direction: active.direction === 'desc' ? 'asc' : 'desc' } as const;
};

export const getQcSortParamUpdates = (
  active: SortState | undefined,
  next: SortState[],
): Record<
  | 'sort_by_call_start_time'
  | 'sort_by_aggregate_score'
  | 'sort_by_call_duration'
  | 'sort_by_section_score',
  string | undefined
> => {
  const { field = '', direction = 'desc' } = getClickedSort(active, next) ?? {};
  const sortParam = String(direction === 'asc' ? Sort.SORT_ASC : Sort.SORT_DESC);
  const sectionId = field.startsWith(SECTION_SCORE_SORT_FIELD_PREFIX)
    ? field.slice(SECTION_SCORE_SORT_FIELD_PREFIX.length)
    : '';

  return {
    sort_by_call_start_time: field === 'call_start_time' ? sortParam : undefined,
    sort_by_aggregate_score: field === 'aggregate_score' ? sortParam : undefined,
    sort_by_call_duration: field === 'call_duration' ? sortParam : undefined,
    sort_by_section_score: sectionId ? `${sectionId}:${direction}` : undefined,
  };
};

// AQ-649: call duration is sorted in ClickHouse only; the API answers 503 when it can't serve
// the sort (no Mongo fallback).
export const isCallDurationSortUnavailable = (sort: Sort, error: unknown) =>
  sort !== Sort.SORT_NONE && isAxiosError(error) && error.response?.status === 503;
